// src/Profile.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, auth, googleProvider } from './firebase'; 
import { signInWithPopup, signOut } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, setDoc, updateDoc, collection, getDocs, query, where, runTransaction, addDoc, orderBy, startAt, endAt, limit, getDoc, deleteField } from 'firebase/firestore';
import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';
import { COLORS } from './theme';
import { getMemberName, getUserRank, DEFAULT_RANKS, formatCurrency, normalizeRanks } from './utils';
import NotificationToggle from './components/NotificationToggle'; 
import './App.css'; 

const POSITIONS = {
    '未設定': ['未設定'],
    'FW': ['CF', 'ST', 'LWG', 'RWG'],
    'MF': ['OMF', 'CMF', 'DMF', 'LSH', 'RSH'],
    'DF': ['CB', 'LSB', 'RSB'],
    'GK': ['GK']
};

const StatCard = ({ label, value, subValue, icon, color }) => (
  <div className="card" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      flex: 1, 
      borderTop: `4px solid ${color}`,
      padding: '15px 5px',
      margin: 0 
  }}>
    <div style={{ fontSize: '24px', marginBottom: '5px' }}>{icon}</div>
    <div style={{ fontSize: '10px', color: '#999', fontWeight: 'bold' }}>{label}</div>
    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>{value}</div>
    {subValue && <div style={{ fontSize: '9px', color: '#aaa' }}>{subValue}</div>}
  </div>
);

const getRankTitle = (games) => {
    if (games >= 100) return { title: 'レジェンド', color: '#FFD700' };
    if (games >= 50) return { title: 'ベテラン', color: '#9C27B0' };
    if (games >= 20) return { title: 'レギュラー', color: '#2196F3' };
    if (games >= 5) return { title: 'ルーキー', color: '#4CAF50' };
    return { title: '見習い', color: '#999' };
};

const getStatColor = (stat) => {
    if(stat === 'offense') return '#ff5252';
    if(stat === 'defense') return '#448aff';
    if(stat === 'stamina') return '#69f0ae';
    if(stat === 'speed') return '#ffeb3b';
    if(stat === 'technique') return '#e040fb';
    return '#fff';
};

const Profile = ({ user, events = [], appConfig, onBack, setActiveTab }) => {
  const [loading, setLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]); 
  
  // ★ 初期ロード時のID協力フラグを記録（移行チェック用）
  const initialCoopRef = useRef(false);
  // ★ private の読み込みに成功したか。失敗時に保存すると
  //   空の formData で private を上書きしてしまうため、ガードに使う。
  const privateLoadedRef = useRef(false);
  
  // ★追加: 招待ボーナスの金額。管理画面（settings/config の inviteBonus）で変更できる。
  //   Functions 側の DEFAULT_INVITE_BONUS と既定値を揃えること。
  const inviteBonus = useMemo(() => {
      const ib = appConfig?.inviteBonus || {};
      const num = (v) => {
          const n = Number(v);
          return (Number.isInteger(n) && n >= 0 && n <= 100000) ? n : 500;
      };
      return { inviter: num(ib.inviter), invitee: num(ib.invitee) };
  }, [appConfig?.inviteBonus]);

  const pointName = appConfig?.pointNameLabel || 'RB Coin';
  const pointUnit = appConfig?.pointUnitLabel || 'RB';

  const [formData, setFormData] = useState({
    displayName: '', 
    number: '', 
    dominantFoot: '右',
    mainPosition: '未設定', 
    subPosition: '未設定', 
    height: '', 
    weight: '', 
    playStyle: '', 
    favoriteTeam: '', 
    bio: '',
    phoneNumber: '', 
    email: '', 
    paypayId: '', 
    isPublicHeight: true, 
    isPublicWeight: false,
    birthDate: '', 
    gender: '未設定', 
    idCooperation: false,
    isRetired: false 
  });

  const [baseStats, setBaseStats] = useState({ offense: 0, defense: 0, stamina: 0, speed: 0, technique: 0 }); 
  const [draftStats, setDraftStats] = useState({ offense: 0, defense: 0, stamina: 0, speed: 0, technique: 0 }); 

  // ★追加: 保存直後、サーバーからの user 更新（onSnapshot）が届くまでの一瞬、
  //   props の user.availablePoints は古い値のまま。その間に setBaseStats(draftStats) で
  //   差分が0になると「残り = 古いavailablePoints − 0」となり、使ったはずのポイントが
  //   復活して見える。これを防ぐため、保存した残り値をローカルに保持し、
  //   表示計算ではこちらを優先する。user 側が追いついたら null に戻す。
  const [availableOverride, setAvailableOverride] = useState(null);

  // ★ サーバーからの user 更新が保存値に追いついたら override を解除する。
  //   以降は user.availablePoints（特訓や試合確定の加算を含む最新値）が表示を支配する。
  useEffect(() => {
      if (availableOverride === null) return;
      if (typeof user?.availablePoints === 'number' && user.availablePoints === availableOverride) {
          setAvailableOverride(null);
      }
  }, [user?.availablePoints, availableOverride]);

  useEffect(() => {
    if (user?.userId) {
      // ① まずは親ドキュメント（user）にあるデータでフォームを初期化
      setFormData({
        displayName: user.displayName || '',
        number: user.number || '', 
        mainPosition: user.mainPosition || '未設定', 
        subPosition: user.subPosition || '未設定',   
        dominantFoot: user.dominantFoot || '右',
        height: user.height || '',
        weight: user.weight || '',
        playStyle: user.playStyle || '',
        favoriteTeam: user.favoriteTeam || '',
        bio: user.bio || '',
        isPublicHeight: user.isPublicHeight !== undefined ? user.isPublicHeight : true,
        isPublicWeight: user.isPublicWeight !== undefined ? user.isPublicWeight : false,
        gender: user.gender || '未設定', 
        isRetired: user.isRetired || false,
        
        // ▼ 以下の機密項目は、移行前ユーザーのためのフォールバック（あとでサブコレクションから上書き）
        phoneNumber: user.phoneNumber || '', 
        email: user.email || '',            
        paypayId: user.paypayId || '',      
        birthDate: user.birthDate || '', 
        idCooperation: user.idCooperation || false, 
      });
      
      const loadedStats = user.baseStats || { offense: 0, defense: 0, stamina: 0, speed: 0, technique: 0 };
      setBaseStats(loadedStats);
      setDraftStats(loadedStats);
      setAvailableOverride(null); // ★ 別ユーザーを開いたら override をリセット

      // ② サブコレクション（安全な金庫）から機密データを取得してフォームを上書き
      const fetchPrivateData = async () => {
          try {
              const privateRef = doc(db, 'users', user.userId, 'private', 'profile');
              const privateSnap = await getDoc(privateRef);
              privateLoadedRef.current = true;   // ★ 読み込みに成功したことを記録
              
              if (privateSnap.exists()) {
                  const p = privateSnap.data();
                  setFormData(prev => ({
                      ...prev,
                      phoneNumber: p.phoneNumber ?? prev.phoneNumber,
                      email:       p.email       ?? prev.email,
                      paypayId:    p.paypayId    ?? prev.paypayId,
                      birthDate:   p.birthDate   ?? prev.birthDate,
                      idCooperation: p.idCooperation ?? prev.idCooperation,
                  }));
                  initialCoopRef.current = p.idCooperation ?? user.idCooperation ?? false;
              } else {
                  initialCoopRef.current = user.idCooperation || false;
              }
          } catch (error) {
              console.error("プライベートデータの取得に失敗しました", error);
          }
      };

      fetchPrivateData();
      fetchHistory(user.userId);
    }
  }, [user?.userId]);

  const fetchHistory = async (userId) => {
      try {
          const q = query(
              collection(db, 'point_logs'), 
              where('userId', '==', userId),
              orderBy('createdAt', 'desc'),
              limit(20)
          );
          const snap = await getDocs(q);
          const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setHistoryLogs(logs); 
      } catch (e) {
          console.error("履歴取得エラー（インデックス未作成の可能性があります）", e);
      }
  };

  const stats = useMemo(() => {
      if (!user) return { games: 0, goals: 0, assists: 0, moms: 0, wins: 0, draws: 0, loses: 0, winRate: '0.0' };
      
      const uStats = user.stats || {};
      let games = uStats.participations || 0;
      let goals = uStats.goals || 0;
      let assists = uStats.assists || 0;
      let moms = uStats.moms || 0;
      let wins = 0; let draws = 0; let loses = 0;

      // ★追加: 「まだ確定していない分」だけを別に数える。
      //   確定済みの分は user.matchPoints に経験値として積まれているため、
      //   ポイント計算でここを足すと二重計上になる。
      let pGames = 0; let pGoals = 0; let pAssists = 0; let pMoms = 0;

      if (events && events.length > 0) {
          const myId = user.userId;
          const currentName = user.displayName;
          const d = new Date();
          const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

          events.forEach(ev => {
              if (ev.status === 'cancelled') return;

              const members = ev.members || [];
              const myInfo = members.find(m => (m.userId === myId) || (!m.userId && getMemberName(m) === currentName));
              const nameAtThatTime = myInfo ? getMemberName(myInfo) : currentName;

              let rtPart = myInfo ? 1 : 0;
              let rtGoals = 0;
              let rtAssists = 0;
              let rtMom = (ev.mom === nameAtThatTime) ? 1 : 0;

              if (ev.matchDetails) {
                  ev.matchDetails.forEach(d => {
                      if (d.scorer === nameAtThatTime) rtGoals++;
                      if (d.assist === nameAtThatTime) rtAssists++;
                  });
              }

              const applied = ev.appliedStats || {};
              const myApplied = applied[myId] || {}; 

              let apPart = myApplied.participations || 0;
              let apGoals = myApplied.goals || 0;
              let apAssists = myApplied.assists || 0;
              let apMom = myApplied.moms || 0;

              games += (rtPart - apPart);
              goals += (rtGoals - apGoals);
              assists += (rtAssists - apAssists);
              moms += (rtMom - apMom);

              // 未確定分（＝appliedStats にまだ書かれていない分）
              pGames += (rtPart - apPart);
              pGoals += (rtGoals - apGoals);
              pAssists += (rtAssists - apAssists);
              pMoms += (rtMom - apMom);

              if (ev.isoDate < today && myInfo) {
                  const type = ev.eventType || 'training';
                  if (type === 'match' || type === 'official') {
                      if (ev.homeScore !== undefined && ev.awayScore !== undefined && ev.homeScore !== '' && ev.awayScore !== '') {
                          const home = Number(ev.homeScore);
                          const away = Number(ev.awayScore);
                          if (myInfo.team === 'white') {
                              if (home > away) wins++; else if (home < away) loses++; else draws++;
                          } else if (myInfo.team === 'black') {
                              if (away > home) wins++; else if (away < home) loses++; else draws++;
                          }
                      }
                  }
              }
          });
      }

      const winRate = (wins + loses + draws) > 0 ? ((wins / (wins + loses + draws)) * 100).toFixed(1) : '0.0';
      return { 
          games: Math.max(0, games), 
          goals: Math.max(0, goals), 
          assists: Math.max(0, assists), 
          moms: Math.max(0, moms), 
          wins, draws, loses, winRate,
          // 未確定分（翌日の自動確定でポイントとして確定する）
          pending: {
              games: Math.max(0, pGames),
              goals: Math.max(0, pGoals),
              assists: Math.max(0, pAssists),
              moms: Math.max(0, pMoms),
          }
      };
  }, [user, events]);

  // ★追加: 試合成績の配点。管理画面（settings/config の matchPoints）で変更できる。
  //   Functions 側の DEFAULT_MATCH_POINTS と既定値を揃えること。
  const matchPointConfig = useMemo(() => {
      const mp = appConfig?.matchPoints || {};
      const num = (v, d) => {
          const n = Number(v);
          return Number.isFinite(n) ? n : d;
      };
      return {
          participation: num(mp.participation, 2),
          goal:          num(mp.goal, 2),
          assist:        num(mp.assist, 1),
          mom:           num(mp.mom, 5),
      };
  }, [appConfig?.matchPoints]);

  // まだ確定していない試合ぶんのポイント（翌日 03:00 に確定して matchPoints に積まれる）
  const pendingPoints = useMemo(() => {
      const p = stats.pending || { games: 0, goals: 0, assists: 0, moms: 0 };
      return (p.games * matchPointConfig.participation)
           + (p.goals * matchPointConfig.goal)
           + (p.assists * matchPointConfig.assist)
           + (p.moms * matchPointConfig.mom);
  }, [stats, matchPointConfig]);

  // ★変更: 残りポイントを「加算方式」に変更した。
  //
  //   【これまでの問題】
  //     残り = 100 + matchPoints + trainingPoints − baseStats合計
  //     という式で毎回ゼロから計算していた。過去に遡って総額を出すため、
  //     計算式や stats の読み方が変わると全員が一斉にマイナスへ振れ、
  //     「特訓しても増えない」状態になっていた。
  //
  //   【新しい方式】
  //     availablePoints という1つの値を保存し、それを増減させる。
  //       ・特訓する         → availablePoints += 特訓ぶん（Function側）
  //       ・試合が確定する   → availablePoints += 試合ぶん（Function側）
  //       ・能力に振り分ける → availablePoints −= 振り分けたぶん（保存時）
  //     過去に遡らないので、二度と一斉マイナスは起きない。
  //
  //   availablePoints は「まだ baseStats に振っていない残り」。
  //   画面上のドラフト操作（draftStats）で仮に振った分を引いて表示する。
  //
  //   移行前（availablePoints 未設定）のユーザーは、従来式でフォールバック計算する。
  //   管理画面の移行を実行すると全員 availablePoints を持つようになる。
  const baseAvailable = useMemo(() => {
      if (!user) return 0;
      // ★ 保存直後はローカルの override を最優先（stale props 対策）
      if (availableOverride !== null) {
          return availableOverride;
      }
      if (typeof user.availablePoints === 'number') {
          // ★重要: pendingPoints（未確定の試合ぶん）を足してはいけない。
          //   availablePoints には pending は含まれていない。表示にだけ足すと、
          //   その分が availablePoints から引かれないまま振り分けに使え、
          //   保存しても復活する＝無限増殖になる（実際に発生済み）。
          //   試合ぶんは翌日03:00の確定で availablePoints に正式加算される。
          return user.availablePoints;
      }
      // 未移行フォールバック（従来式）
      const confirmed = Number(user.matchPoints) || 0;
      const training = Number(user.trainingPoints) || 0;
      const allocatedBase = (user.baseStats?.offense||0)+(user.baseStats?.defense||0)+(user.baseStats?.stamina||0)+(user.baseStats?.speed||0)+(user.baseStats?.technique||0);
      return 100 + confirmed + pendingPoints + training - allocatedBase;
  }, [user, pendingPoints, availableOverride]);

  // 画面でこのセッション中に仮振りした分を差し引いた「いま使える残り」
  const unallocatedPoints = useMemo(() => {
      const allocatedNow = (draftStats.offense - baseStats.offense)
                         + (draftStats.defense - baseStats.defense)
                         + (draftStats.stamina - baseStats.stamina)
                         + (draftStats.speed - baseStats.speed)
                         + (draftStats.technique - baseStats.technique);
      return baseAvailable - allocatedNow;
  }, [baseAvailable, draftStats, baseStats]);

  if (!user) {
      return (
          <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
              <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
              <div>読み込み中...</div>
          </div>
      );
  }

  const currentRank = getUserRank(user, appConfig?.ranks || DEFAULT_RANKS);
  const playerRank = getRankTitle(stats.games);
  const isStaff = user.role === 'staff' || user.role === 'admin';

  const isEligibleForInvite = (() => {
      if (user.invitedBy) return false; 
      if (!user.createdAt) return false; 
      
      const createdDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
      if (isNaN(createdDate.getTime())) return false;

      const today = new Date();
      const diffTime = today.getTime() - createdDate.getTime();
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      return diffDays <= 30;
  })();

  const handleAddStat = (statName) => {
      if (unallocatedPoints > 0) {
          setDraftStats(prev => ({ ...prev, [statName]: prev[statName] + 1 }));
      }
  };

  const handleSubStat = (statName) => {
      if (draftStats[statName] > baseStats[statName]) {
          setDraftStats(prev => ({ ...prev, [statName]: prev[statName] - 1 }));
      }
  };

  const handleLinkGoogle = async () => {
      try {
          const result = await signInWithPopup(auth, googleProvider);
          const gUser = result.user;

          const qCheck = query(collection(db, 'users'), where('googleUid', '==', gUser.uid));
          const snapCheck = await getDocs(qCheck);

          if (!snapCheck.empty && snapCheck.docs[0].id !== user.userId) {
              toast.error('このGoogleアカウントは既に別のメンバーと連携されています');
              await signOut(auth); 
              return;
          }

          const userRef = doc(db, 'users', user.userId);
          await updateDoc(userRef, {
              googleUid: gUser.uid
          });

          await signOut(auth);
          user.googleUid = gUser.uid;

          Swal.fire({
              title: '連携完了！',
              text: 'Googleアカウントの連携が完了しました。次回からPWA（ホーム画面）でGoogleログインが可能になります！',
              icon: 'success',
              confirmButtonText: 'OK',
              confirmButtonColor: COLORS.primary
          });

      } catch (error) {
          console.error("Google連携エラー:", error);
          if (error.code !== 'auth/popup-closed-by-user') {
              toast.error("連携に失敗しました");
          }
      }
  };

 const handleSave = async () => {
    if (!formData.displayName.trim()) { toast.error('表示名は必須です'); return; }

    const isNewlyCooperated = !initialCoopRef.current && formData.idCooperation;

    setLoading(true);
    try {
      const timestamp = new Date().toISOString();
      const { rankId: normalizedRankId, rankIds: normalizedRankIds, membership: normalizedMembership } = normalizeRanks(user);
      
      const userRef = doc(db, 'users', user.userId);
      const privateRef = doc(db, 'users', user.userId, 'private', 'profile');
      const publicUserRef = doc(db, 'users_public', user.userId);

      // ★ 第1段：退避用（安全な金庫への保管）

      // ★ この画面に入力欄が無い項目（lineId / transferPassword）は、絶対に送らない。
      //   setDoc(..., { merge: true }) は「指定しなかった項目はそのまま残す」ため、
      //   書かなければ private 内の値は保持される。
      //   以前は user.lineId || '' と書いていたが、第7段で親から削除した後は
      //   user.lineId が undefined になり、保存のたびに private の実データを
      //   空文字で上書きして消してしまう。
      const privateData = {
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          paypayId: formData.paypayId,
          birthDate: formData.birthDate,
          updatedAt: timestamp
      };

      // ★ 第2段：親ドキュメント用（デュアルライト期間：消さずに両方に書く）
      // ★追加: この保存で新たに振り分けた分を availablePoints から引く。
      //   draftStats（画面の値）− baseStats（保存済みの値）＝ 今回振った分。
      //   「保存した数値は下げられない」仕様なので、この差は常に 0 以上。
      const allocatedNow =
            (draftStats.offense   - baseStats.offense)
          + (draftStats.defense   - baseStats.defense)
          + (draftStats.stamina   - baseStats.stamina)
          + (draftStats.speed     - baseStats.speed)
          + (draftStats.technique - baseStats.technique);

      // 移行前ユーザーは availablePoints をここで初期化する（従来式の残り − 今回振った分）。
      let nextAvailable;
      if (typeof user.availablePoints === 'number') {
          nextAvailable = Math.max(0, user.availablePoints - allocatedNow);
      } else {
          const confirmed = Number(user.matchPoints) || 0;
          const training = Number(user.trainingPoints) || 0;
          const allocatedBase = (user.baseStats?.offense||0)+(user.baseStats?.defense||0)+(user.baseStats?.stamina||0)+(user.baseStats?.speed||0)+(user.baseStats?.technique||0);
          const legacyRemaining = 100 + confirmed + training - allocatedBase; // pendingは確定時に別途入るのでここでは含めない
          nextAvailable = Math.max(0, legacyRemaining - allocatedNow);
      }

      // ★保存直前のサニタイズ（二重の安全網）:
      //   背番号を 0〜999 の整数文字列に強制する。入力欄のガードを何らかの理由で
      //   すり抜けても、壊れた値（電話番号・小数・巨大連結値）をDBに書かせない。
      const safeNumber = (() => {
          const digits = String(formData.number ?? '').replace(/[^0-9]/g, '');
          if (digits === '') return '';
          const n = Math.min(999, parseInt(digits.slice(0, 3), 10));
          return String(n);
      })();

      const publicUpdates = {
          displayName: formData.displayName,
          number: safeNumber,
          mainPosition: formData.mainPosition,
          subPosition: formData.subPosition,
          dominantFoot: formData.dominantFoot,
          height: formData.height,
          weight: formData.weight,
          playStyle: formData.playStyle,
          favoriteTeam: formData.favoriteTeam,
          bio: formData.bio,
          isPublicHeight: formData.isPublicHeight,
          isPublicWeight: formData.isPublicWeight,
          gender: formData.gender,
          isRetired: formData.isRetired,
          baseStats: draftStats,
          availablePoints: nextAvailable, // ★加算方式の残り
          idCooperation: formData.idCooperation, // ★親に残す（一覧表示用）
          updatedAt: timestamp,
          
          // ★ 第6段：機密は private のみに書く。
          //   ここで親に書き戻すと、第7段で削除しても
          //   次のプロフィール保存で復活してしまう。
      };
      
      // ★ 読み込みに失敗している場合は formData が空の可能性があるので、機密は書かない
      if (privateLoadedRef.current) {
          await setDoc(privateRef, privateData, { merge: true });
      } else {
          console.warn('private の読み込み未完了のため、連絡先の保存をスキップしました');
          toast.error('連絡先情報の保存には再読み込みが必要です');
      }

      // ★★★ 最重要: 能力値と availablePoints の保存。
      //   ここが Firestore ルールや認証UIDの不一致で失敗すると、
      //   保存できていないのに画面だけポイントが増減してしまう（＝復活バグ）。
      //   失敗したら例外が飛ぶので、下の setBaseStats / setAvailableOverride は
      //   実行されず、ローカル状態も変えない。ユーザーには保存失敗を明示する。
      try {
          await updateDoc(userRef, publicUpdates);
      } catch (writeErr) {
          console.error('能力値の保存に失敗（権限エラーの可能性）:', writeErr);
          // ドラフトを保存前の状態へ戻し、増えたまま固定されるのを防ぐ
          setDraftStats(baseStats);
          setAvailableOverride(null);
          toast.error('ステータスを保存できませんでした。一度ログアウトして入り直してください。');
          setLoading(false);
          return; // ここで中断（成功トーストも users_public 書き込みもしない）
      }
      
      // ★ 3. ユーザー公開用ドキュメント（App.jsの同期ズレ防止用・省略なし）
      const publicData = {
          userId: user.userId,
          displayName: formData.displayName,
          pictureUrl: user.pictureUrl || null, 
          bio: formData.bio,
          mainPosition: formData.mainPosition,
          subPosition: formData.subPosition,
          number: safeNumber,
          dominantFoot: formData.dominantFoot,
          playStyle: formData.playStyle,
          favoriteTeam: formData.favoriteTeam,
          gender: formData.gender,
          height: formData.isPublicHeight ? formData.height : null,
          weight: formData.isPublicWeight ? formData.weight : null,
          baseStats: draftStats,
          stats: user.stats || {}, 
          isRetired: formData.isRetired,
          updatedAt: timestamp,
          points: user.points || 0,
          totalPaid: user.totalPaid || 0,
          rankId: normalizedRankId,
          rankIds: normalizedRankIds,
          rankExpiries: user.rankExpiries || {},
          cards: user.cards || [],
          deck: user.deck || {},
          trainingPoints: user.trainingPoints || 0,
          matchPoints: user.matchPoints || 0,
          availablePoints: nextAvailable, // ★加算方式の残り
          role: user.role || 'member',
          membership: normalizedMembership,
          lastLogin: user.lastLogin || timestamp,
          createdAt: user.createdAt || timestamp,
          lastDecayDate: user.lastDecayDate || null
      };
      
      await setDoc(publicUserRef, publicData, { merge: true });
      setBaseStats(draftStats);
      // ★ 保存した残りをローカルに反映（onSnapshot が届くまでの復活バグ対策）
      setAvailableOverride(nextAvailable);
      initialCoopRef.current = formData.idCooperation;

      if (isNewlyCooperated) {
          Swal.fire('ありがとうございます！', 'ID協力のお申し込みを受け付けました。', 'success');
      } else {
          toast.success('プロフィールとステータスを保存しました！');
      }
    } catch (e) {
      console.error(e);
      toast.error('保存に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleUseInviteCode = async () => {
      const { value: codeInput } = await Swal.fire({
          title: '招待コードを入力',
          text: `紹介してくれた人の招待コードを入力してください（承認されるとあなたに ${inviteBonus.invitee} ${pointUnit} 付与されます）`,
          input: 'text',
          inputPlaceholder: '例: AbCdEf123...',
          showCancelButton: true,
          confirmButtonText: '適用する'
      });

      if (!codeInput) return;
      
      const code = codeInput.trim();

      if (code === user.userId) {
          toast.error('自分の招待コードは使えません');
          return;
      }

      setLoading(true);
      try {
          await runTransaction(db, async (t) => {
              const inviterRef = doc(db, 'users', code);
              const inviterDoc = await t.get(inviterRef);
              
              if (!inviterDoc.exists()) {
                  throw new Error("Invalid Code");
              }

              const myRef = doc(db, 'users', user.userId);
              const myDoc = await t.get(myRef);
              const myData = myDoc.data();

              if (myData.invitedBy) {
                  throw new Error("Already Invited");
              }

              const inviterData = inviterDoc.data();
              
              t.update(myRef, { 
                  invitedBy: code, 
                  invitedAt: new Date().toISOString()
              });

              const requestRef = doc(collection(db, 'invite_requests'));
              t.set(requestRef, {
                  newUserId: user.userId,
                  newUserName: myData.displayName || '名無し',
                  inviterId: code,
                  inviterName: inviterData.displayName || '名無し',
                  // ★変更: 500 の直書きをやめ、管理画面の設定から読む。
                  //   ここで保存した金額が承認時にそのまま使われるため、
                  //   期間限定の金額は「申請した時点」で確定する。
                  bonusAmount: inviteBonus.inviter,
                  inviterBonus: inviteBonus.inviter,
                  inviteeBonus: inviteBonus.invitee,
                  status: 'pending',
                  createdAt: new Date().toISOString()
              });
          });

          Swal.fire('申請完了！', `招待コードを送信しました。\n運営の承認後、あなたに ${inviteBonus.invitee} ${pointUnit}、紹介者に ${inviteBonus.inviter} ${pointUnit} が付与されます！`, 'success');

      } catch (e) {
          console.error(e);
          if (e.message === "Invalid Code") toast.error('無効な招待コードです');
          else if (e.message === "Already Invited") toast.error('既に招待コードを使用済みです');
          else toast.error('エラーが発生しました');
      } finally {
          setLoading(false);
      }
  };

  const handleShareCode = () => {
      // ★変更: 共有文面にも実際の金額を入れる（管理画面の設定に追従する）
      const text = `「リベンジャーズアプリ⚽」\n💌招待コード\n【${user.userId}】\n\n【登録はこちら】\n${inviteBonus.invitee}${pointUnit}をゲット！\n📱LINE公式アカウント\nhttps://lin.ee/05SHtkL\n\nPC、直接はこちら💁\nhttps://revengersblack.web.app/`;
      const url = `https://line.me/R/msg/text/?${encodeURIComponent(text)}`;
      window.open(url, '_blank');
  };

  const getBonusText = (amount) => {
      const num = Number(amount);
      if (num >= 5000) {
          const bonus = num * 0.1;
          return `${formatCurrency(num + bonus)} ${pointUnit} (+10% BONUS!)`;
      }
      return `${formatCurrency(num)} ${pointUnit}`;
  };

  const getAmountOptions = () => ({
      '1000': `1,000円 (${getBonusText(1000)})`,
      '3000': `3,000円 (${getBonusText(3000)})`,
      '5000': `5,000円 (${getBonusText(5000)})`,
      '10000': `10,000円 (${getBonusText(10000)})`
  });

  const handleRequestCharge = async () => {
      const adminId = appConfig?.adminPayPayId || ''; 
      
      const { value: amount } = await Swal.fire({
          title: `${pointName}購入 (PayPay)`,
          text: '5,000円以上で10%ボーナス付与！',
          input: 'select',
          inputOptions: getAmountOptions(),
          inputPlaceholder: '金額を選択してください',
          showCancelButton: true,
          confirmButtonText: '次へ',
          customClass: { popup: 'swal-wide-popup' } 
      });
      if (!amount) return;

      let adminIdHtml = '';
      if(adminId) {
          adminIdHtml = `
            <div style="background:#f9f9f9; padding:10px; border-radius:8px; margin-bottom:10px; border:1px solid #ddd;">
               <div style="font-size:10px; color:#666;">送金先 (管理者ID)</div>
               <div style="font-weight:bold; font-size:16px; margin-bottom:5px;">${adminId}</div>
               <button type="button" id="copy-admin-id" style="background:#333; color:white; border:none; border-radius:4px; padding:4px 8px; font-size:12px; cursor:pointer;">IDをコピー</button>
            </div>
          `;
      }

      const result = await Swal.fire({
          title: 'PayPay送金',
          html: `
            <div style="text-align:left; font-size:14px;">
              ${adminIdHtml}
              <p>1. 下のボタンからPayPayを起動し、<b>「${Number(amount).toLocaleString()}円」</b>を送金してください。</p>
              <p>2. <span style="color:#e65100; font-weight:bold;">送金完了後、必ずあなたのPayPay IDを入力して</span>申請ボタンを押してください。</p>
              <p style="margin-top:10px; font-weight:bold; color:#e65100;">あなたのPayPay ID (必須)</p>
            </div>
          `,
          input: 'text',
          inputValue: formData.paypayId || '',
          inputPlaceholder: '例: my_paypay_id',
          inputValidator: (value) => {
              if (!value) {
                  return '入金確認のため、PayPay IDの入力は必須です';
              }
          },
          footer: '<small>※IDを入力しないと申請できません</small>',
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'アプリ起動 & 申請へ',
          confirmButtonColor: '#FF0033',
          cancelButtonText: '閉じる',
          didOpen: () => {
              const btn = document.getElementById('copy-admin-id');
              if(btn) {
                  btn.addEventListener('click', () => {
                      if (navigator.clipboard) {
                          navigator.clipboard.writeText(adminId);
                      } else {
                          const textArea = document.createElement("textarea");
                          textArea.value = adminId;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand("copy");
                          document.body.removeChild(textArea);
                      }
                      btn.textContent = 'コピーしました！';
                      setTimeout(() => btn.textContent = 'IDをコピー', 2000);
                  });
              }
              const b = Swal.getHtmlContainer()?.querySelector('b');
              if(b) b.style.color = '#FF0033';
          }
      });

      if (result.isConfirmed) {
          const inputId = result.value.trim();
          window.open('https://paypay.ne.jp/', '_blank');

          const confirmResult = await Swal.fire({
              title: '送金はお済みですか？',
              text: `ID: ${inputId} で申請します。`,
              icon: 'question',
              showCancelButton: true,
              confirmButtonText: 'はい、申請します',
              confirmButtonColor: COLORS.success,
              cancelButtonText: 'まだです'
          });

          if (confirmResult.isConfirmed) {
              try {
                  // ★ PayPay IDが変わった場合も、新しい金庫（サブコレクション）に保存する
                  if (inputId !== formData.paypayId) {
                      const privateRef = doc(db, 'users', user.userId, 'private', 'profile');
                      await setDoc(privateRef, { paypayId: inputId }, { merge: true });
                      // 念のため親ドキュメントからも削除しておく
                      await updateDoc(doc(db, 'users', user.userId), { paypayId: deleteField() }).catch(() => {});
                      setFormData(prev => ({ ...prev, paypayId: inputId }));
                  }
                  await addDoc(collection(db, 'charge_requests'), {
                      userId: user.userId,
                      userName: user.displayName,
                      amount: Number(amount),
                      paypayId: inputId,
                      method: 'paypay',
                      status: 'pending', 
                      createdAt: new Date().toISOString()
                  });
                  Swal.fire('申請完了', '管理者に通知しました。', 'success');
              } catch (e) { 
                  console.error(e);
                  toast.error('申請に失敗しました'); 
              }
          }
      }
  };

  const handleBankTransferRequest = async () => {
      const bankInfo = appConfig?.bankInfo || '振込先情報が設定されていません。';
      const { value: amount } = await Swal.fire({
          title: `${pointName}購入 (銀行振込)`,
          text: '5,000円以上で10%ボーナス付与！',
          input: 'select',
          inputOptions: getAmountOptions(),
          inputPlaceholder: '金額を選択してください',
          showCancelButton: true,
          confirmButtonText: '次へ',
          customClass: { popup: 'swal-wide-popup' }
      });
      if (!amount) return;

      const { value: transferName } = await Swal.fire({
          title: '振込名義の入力',
          html: `<div style="text-align:left; font-size:12px; background:#f5f5f5; padding:10px;">${bankInfo}</div>`,
          input: 'text',
          inputValue: user.displayName || '', 
          showCancelButton: true,
          confirmButtonText: '確認へ進む'
      });
      if (!transferName) return;

      const result = await Swal.fire({
          title: '申請の確認',
          html: `<p>支払: <b>${Number(amount).toLocaleString()}円</b></p><p>名義: <b>${transferName}</b></p><p><span style="color:#e65100; font-weight:bold;">[注意]お振込後に申請</span></p>`,
          showCancelButton: true,
          confirmButtonText: '申請する',
          confirmButtonColor: COLORS.secondary
      });

      if (result.isConfirmed) {
          try {
              await addDoc(collection(db, 'charge_requests'), {
                  userId: user.userId,
                  userName: user.displayName,
                  amount: Number(amount),
                  transferName: transferName,
                  method: 'bank',
                  status: 'pending', 
                  createdAt: new Date().toISOString()
              });
              Swal.fire('申請完了', '振込確認後、RBコインが付与されます。', 'success');
          } catch (e) { toast.error('申請に失敗しました'); }
      }
  };

  const handleTransfer = async () => {
      const currentPoints = user.points || 0;
      if (currentPoints <= 0) {
          toast.error(`${pointUnit}がありません`);
          return;
      }

      const { value: keyword } = await Swal.fire({
          title: '送金相手を検索',
          text: '名前の「最初の文字」を入力してください（前方一致検索）',
          input: 'text',
          inputPlaceholder: '例: 田中',
          showCancelButton: true,
          confirmButtonText: '検索'
      });

      if (!keyword) return;

      const trimmedKeyword = keyword.trim();
      
      const q = query(
          collection(db, 'users'), 
          orderBy('displayName'),
          startAt(trimmedKeyword),
          endAt(trimmedKeyword + '\uf8ff'),
          limit(20)
      );
      
      const snapshot = await getDocs(q);
      const searchResults = {};
      snapshot.forEach(doc => {
          const u = doc.data();
          if (u.userId !== user.userId) { 
              searchResults[u.userId] = u.displayName || '名無し';
          }
      });

      if (Object.keys(searchResults).length === 0) {
          toast.error('該当するユーザーが見つかりませんでした');
          return;
      }

      const { value: targetUserId } = await Swal.fire({
          title: '送金相手を選択',
          input: 'radio',
          inputOptions: searchResults,
          inputValidator: (value) => {
              if (!value) return '相手を選択してください';
          },
          showCancelButton: true,
          confirmButtonText: '次へ'
      });

      if (!targetUserId) return;
      const targetName = searchResults[targetUserId];

      const { value: amountStr } = await Swal.fire({
          title: `${targetName} さんへ送る`,
          text: `現在の所持: ${formatCurrency(currentPoints)} ${pointUnit}`,
          input: 'number',
          inputPlaceholder: `送る${pointUnit}数`,
          showCancelButton: true,
          confirmButtonText: '確認',
          inputValidator: (value) => {
              if (!value || Number(value) <= 0) return '1以上の数値を入力してください';
              if (Number(value) > currentPoints) return '所持RBコインが足りません';
          }
      });

      if (!amountStr) return;
      const amount = parseInt(amountStr);

      const { value: message } = await Swal.fire({
          title: 'メッセージ (任意)',
          input: 'text',
          inputPlaceholder: '例: 昨日のジュース代！',
          showCancelButton: true,
          confirmButtonText: '送金',
          confirmButtonColor: '#e65100'
      });

      // ★ 送金は Cloud Functions に任せる。
      //   以前はクライアントから相手の users を直接書き換えていたため、
      //   ルールで他人の更新を禁止すると動かなくなっていた。
      //   サーバー側で送金元を request.auth.uid から取るので、なりすましもできない。
      setLoading(true);
      try {
          const fns = getFunctions(getApp(), 'asia-northeast1');
          const call = httpsCallable(fns, 'transferPoints');
          await call({
              targetUserId: targetUserId,
              amount: amount,
              memo: message || ''
          });

          Swal.fire('完了', `${targetName}さんに ${amount} ${pointUnit} 送りました！`, 'success');
          fetchHistory(user.userId);

      } catch (e) {
          console.error("送金エラー:", e?.code || '', e?.message || e);
          // Functions が返す HttpsError は message に理由が入っている
          toast.error(e?.message || '処理に失敗しました');
      } finally {
          setLoading(false);
      }
  };


  const handleLogout = async () => {
      try {
          await signOut(auth);
          
          if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map(k => caches.delete(k)));
          }
          if (navigator.serviceWorker) {
              const regs = await navigator.serviceWorker.getRegistrations();
              await Promise.all(regs.map(r => r.unregister()));
          }
      } catch (error) {
          console.error("ログアウト処理中にエラーが発生しました:", error);
      } finally {
          localStorage.clear();
          window.location.replace(window.location.origin);
      }
  };

  const displayPosition = formData.mainPosition !== '未設定' 
        ? `${formData.mainPosition} (${formData.subPosition})` 
        : '⚠️ ポジション未設定';
        
  const currentSubPositions = POSITIONS[formData.mainPosition] || POSITIONS['未設定'];

  return (
    <div className="container">
      <Toaster position="top-center" />
      
      <div className="card" style={{ borderRadius: '0 0 20px 20px', textAlign: 'center', marginBottom: '20px', marginTop:'-10px', paddingTop:'40px' }}>
        <button onClick={onBack} className="btn" style={{ position: 'absolute', left: '20px', top: '20px', background: 'none', border: 'none', color: COLORS.primary, fontSize: '16px', padding:0 }}>
          ← 戻る
        </button>
        <h2 style={{ margin: 0, fontSize: '20px', color: COLORS.text }}>マイページ</h2>
      </div>

      <div style={{ padding: '0 20px' }}>
        <div className="card" style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
          <div style={{position:'relative'}}>
              {user.pictureUrl ? <img src={user.pictureUrl} alt="" style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '10px', objectFit:'cover', border:`3px solid ${currentRank.priority ? COLORS.secondary : '#eee'}` }} /> : <div style={{ width: '80px', height: '80px', borderRadius: '50%', marginBottom: '10px', background:'#eee', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'30px'}}>👤</div>}
              <div className="badge" style={{position:'absolute', bottom:0, right:-10, background:playerRank.color, color:'white', border:'2px solid white'}}>{playerRank.title}</div>
          </div>
          
          <div style={{ fontWeight: 'bold', fontSize: '18px' }}>{user.displayName}</div>
          
          <div style={{ fontSize: '12px', color: formData.mainPosition === '未設定' ? '#d32f2f' : '#666', fontWeight: formData.mainPosition === '未設定' ? 'bold' : 'normal' }}>
              {displayPosition} / No.{formData.number || '-'}
          </div>
          
          <div style={{marginTop:'8px', display:'flex', gap:'5px', flexWrap:'wrap', justifyContent:'center'}}>
              <span className="badge" style={{background: currentRank.priority ? '#fff8e1' : '#f5f5f5', color: currentRank.priority ? '#f57f17' : '#666', border: `1px solid ${currentRank.priority ? '#ffe0b2' : '#ddd'}`}}>
                  {currentRank.icon} {currentRank.name}
              </span>
              {isStaff && <span className="badge" style={{background:'#e3f2fd', color:'#1565c0', border:'1px solid #bbdefb'}}>🛡️ 運営</span>}
          </div>
        </div>

        {isStaff && (
            <div style={{marginBottom:'20px'}}>
                <button onClick={() => setActiveTab('admin')} className="btn" style={{background:'#212121', color:'white', fontWeight:'bold', border:'none', width: '100%', padding: '15px', borderRadius: '12px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)'}}>
                    ⚙️ 管理者ダッシュボードを開く
                </button>
            </div>
        )}

        <div className="card" style={{background:'linear-gradient(135deg, #e65100, #ff9800)', color:'white', boxShadow:'0 4px 10px rgba(230, 81, 0, 0.3)'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'15px'}}>
                <div>
                    <div style={{fontSize:'11px', opacity:0.9, marginBottom:'2px'}}>所持 {pointName}</div>
                    <div style={{fontSize:'28px', fontWeight:'bold'}}>{user.points ? user.points.toLocaleString() : 0} <span style={{fontSize:'14px'}}>{pointUnit}</span></div>
                </div>
                <img src={`${process.env.PUBLIC_URL}/rbcoin.png`} alt="coin" style={{width:'60px', height:'60px', objectFit:'contain'}} />
            </div>
            <div style={{display:'flex', gap:'10px'}}>
                <button onClick={handleRequestCharge} className="btn" style={{flex:1, background:'white', color:'#e65100', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px', fontSize:'10px', fontWeight:'bold', flexDirection:'column', padding:'8px 0'}}>
                    <span>👛 PayPay購入</span>
                </button>
                <button onClick={handleBankTransferRequest} className="btn" style={{flex:1, background:'rgba(255,255,255,0.2)', color:'white', border:'1px solid rgba(255,255,255,0.5)', fontSize:'10px', fontWeight:'bold', flexDirection:'column', padding:'8px 0'}}>
                    <span>🏦 銀行振込</span>
                </button>
                <button onClick={handleTransfer} className="btn" style={{flex:1, background:'#333', color:'white', border:'none', fontSize:'10px', fontWeight:'bold', flexDirection:'column', padding:'8px 0'}}>
                    <span>💸 送金</span>
                </button>
            </div>
        </div>

        <div className="card" style={{background:'#fff3e0', border:'1px solid #ffe0b2', wordBreak: 'break-word'}}>
            <h3 style={{fontSize:'16px', color:'#e65100', marginBottom:'10px', display:'flex', alignItems:'center'}}>
                🎁 友達招待キャンペーン
            </h3>
            
            <div style={{marginBottom:'15px'}}>
                <div style={{fontSize:'11px', color:'#666', marginBottom:'5px'}}>あなたの招待コード</div>
                <div style={{display:'flex', gap:'5px', alignItems: 'stretch'}}>
                    <div style={{flex:1, background:'white', padding:'10px', fontWeight:'bold', textAlign:'center', fontSize:'14px', letterSpacing:'1px', border:'1px dashed #ccc', borderRadius:'4px', userSelect:'all', wordBreak:'break-all', display:'flex', alignItems:'center', justifyContent:'center'}}>
                        {user.userId}
                    </div>
                    <button onClick={handleShareCode} className="btn" style={{background:'#06c755', color:'white', fontWeight:'bold', padding:'0 15px', flexShrink: 0, display:'flex', alignItems:'center'}}>
                        LINE
                    </button>
                </div>
                <div style={{fontSize:'10px', color:'#e65100', marginTop:'8px', lineHeight:'1.4'}}>
                    {/* ★変更: 500 の直書きをやめ、管理画面の設定を表示する。
                        紹介者と紹介された人で金額が違う場合は、それぞれ出し分ける。 */}
                    {inviteBonus.inviter === inviteBonus.invitee
                        ? `※友達が登録時にコードを入力すると、お互いに${inviteBonus.inviter}${pointUnit}をGET！`
                        : `※友達が登録時にコードを入力すると、あなたに${inviteBonus.inviter}${pointUnit}・お友達に${inviteBonus.invitee}${pointUnit}をGET！`}
                </div>
            </div>

            {isEligibleForInvite && (
                <div style={{marginTop:'15px', paddingTop:'15px', borderTop:'1px dashed #ffe0b2'}}>
                    <div style={{fontSize:'12px', marginBottom:'5px', color:'#e65100', fontWeight:'bold'}}>招待コードをお持ちの方</div>
                    <button onClick={handleUseInviteCode} className="btn" style={{width:'100%', background:'white', border:'1px solid #e65100', color:'#e65100', fontWeight:'bold', justifyContent:'center', padding:'12px 0'}}>
                        {/* ★変更: 500 の直書きをやめ、管理画面の設定を表示する */}
                        コードを入力して{inviteBonus.invitee}{pointUnit} 申請
                    </button>
                </div>
            )}
        </div>

        <div className="card">
            <h3 style={{ fontSize: '14px', marginBottom: '10px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>📜 コイン通帳 (最新20件)</h3>
            <div style={{maxHeight:'200px', overflowY:'auto'}}>
                {historyLogs.length > 0 ? historyLogs.map((log) => (
                    <div key={log.id} style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px dashed #eee', fontSize:'12px'}}>
                        <div style={{flex:1}}>
                            <div style={{color:'#666', fontSize:'10px'}}>{new Date(log.createdAt).toLocaleDateString()} {new Date(log.createdAt).toLocaleTimeString()}</div>
                            <div style={{fontWeight:'bold'}}>{log.reason}</div>
                        </div>
                        <div style={{fontWeight:'bold', color: log.amount > 0 ? '#2e7d32' : '#c62828'}}>
                            {log.amount > 0 ? '+' : ''}{log.amount}
                        </div>
                    </div>
                )) : (
                    <div style={{textAlign:'center', color:'#999', fontSize:'12px', padding:'10px'}}>履歴がありません</div>
                )}
            </div>
        </div>

        <h3 style={{ fontSize: '16px', color: '#555', marginBottom: '10px', paddingLeft: '10px', borderLeft: `4px solid ${COLORS.primary}` }}>📊 個人成績 (通算)</h3>
        <div style={{ display: 'grid', gridTemplateColumns:'repeat(auto-fit, minmax(100px, 1fr))', gap: '10px', marginBottom: '20px' }}>
            <StatCard label="出場数" value={stats.games} icon="🏟️" color="#607D8B" />
            <StatCard label="ゴール" value={stats.goals} subValue={(stats.games > 0 ? (stats.goals/stats.games).toFixed(2) : 0) + " 点/試"} icon="⚽" color="#4CAF50" />
            <StatCard label="アシスト" value={stats.assists} icon="👟" color="#2196F3" />
            <StatCard label="MOM" value={stats.moms} icon="🏅" color="#FFC107" />
        </div>

        <div className="card" style={{ marginBottom: '30px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{textAlign:'center', flex:1}}>
                <div style={{fontSize:'10px', color:'#999', fontWeight:'bold'}}>勝率</div>
                <div style={{fontSize:'24px', fontWeight:'bold', color:COLORS.secondary}}>{stats.winRate}%</div>
            </div>
            <div style={{height:'30px', width:'1px', background:'#eee'}}></div>
            <div style={{flex:2, display:'flex', justifyContent:'space-around'}}>
                <div style={{textAlign:'center'}}><span style={{display:'block', fontSize:'14px', fontWeight:'bold'}}>{stats.wins}</span><span style={{fontSize:'10px', color:'#999'}}>Win</span></div>
                <div style={{textAlign:'center'}}><span style={{display:'block', fontSize:'14px', fontWeight:'bold'}}>{stats.loses}</span><span style={{fontSize:'10px', color:'#999'}}>Lose</span></div>
                <div style={{textAlign:'center'}}><span style={{display:'block', fontSize:'14px', fontWeight:'bold'}}>{stats.draws}</span><span style={{fontSize:'10px', color:'#999'}}>Draw</span></div>
            </div>
        </div>

        <div className="card" style={{ marginBottom: '30px', background: 'radial-gradient(circle, #333 0%, #111 100%)', color: 'white', textAlign: 'center', border: '2px solid #555' }}>
            <h3 style={{ fontSize: '16px', color: '#ffd700', marginBottom: '10px' }}>🎴 ステータス育成パネル</h3>
            <p style={{ fontSize: '11px', color: '#aaa', marginBottom: '20px', lineHeight: '1.5' }}>
                ここで設定した能力が、<br/>
                排出されるあなたのカードの「基礎能力」になります！
            </p>

            <div style={{ background: 'linear-gradient(135deg, #2a2a2a, #111)', padding: '15px', borderRadius: '12px', border: '1px solid #444', marginBottom: '15px' }}>
                <div style={{ textAlign: 'center', marginBottom: '15px', color: '#ffeb3b', fontWeight: 'bold' }}>
                    残り振り分けポイント: <span style={{ fontSize: '24px' }}>{Math.max(0, unallocatedPoints)}</span> P
                </div>
                <p style={{ fontSize: '10px', color: '#ff5252', textAlign: 'center', marginBottom: '15px', fontWeight: 'bold' }}>
                    ※試合に参加して活躍するほどポイントが増えます！<br/>保存した数値を下げることはできません。
                </p>

                {['offense', 'defense', 'stamina', 'speed', 'technique'].map(stat => (
                    <div key={stat} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', padding: '0 10px' }}>
                        <span style={{ width: '40px', fontSize: '14px', fontWeight: 'bold', color: getStatColor(stat), textAlign: 'left' }}>
                            {stat.toUpperCase().slice(0,3)}
                        </span>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <button 
                                onClick={() => handleSubStat(stat)} 
                                disabled={draftStats[stat] <= baseStats[stat]} 
                                style={{ 
                                    width: '32px', height: '32px', borderRadius: '50%', 
                                    background: draftStats[stat] <= baseStats[stat] ? '#555' : '#d32f2f', 
                                    color: 'white', border: 'none', fontSize: '18px', fontWeight: 'bold', 
                                    cursor: draftStats[stat] <= baseStats[stat] ? 'not-allowed' : 'pointer',
                                    boxShadow: draftStats[stat] <= baseStats[stat] ? 'none' : '0 2px 5px rgba(211,47,47,0.5)'
                                }}
                            >
                                -
                            </button>
                            
                            <span style={{ width: '40px', textAlign: 'center', fontSize: '20px', fontWeight: 'bold', color: draftStats[stat] > baseStats[stat] ? '#ffeb3b' : 'white' }}>
                                {draftStats[stat]}
                            </span>
                            
                            <button 
                                onClick={() => handleAddStat(stat)} 
                                disabled={unallocatedPoints <= 0} 
                                style={{ 
                                    width: '32px', height: '32px', borderRadius: '50%', 
                                    background: unallocatedPoints <= 0 ? '#555' : '#1976d2', 
                                    color: 'white', border: 'none', fontSize: '18px', fontWeight: 'bold', 
                                    cursor: unallocatedPoints <= 0 ? 'not-allowed' : 'pointer',
                                    boxShadow: unallocatedPoints <= 0 ? 'none' : '0 2px 5px rgba(25,118,210,0.5)'
                                }}
                            >
                                +
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            
            <button onClick={() => setActiveTab('training')} style={{ width: '100%', background: 'linear-gradient(135deg, #e65100, #ff9800)', color: 'white', border: 'none', padding: '15px', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 10px rgba(230, 81, 0, 0.3)' }}>
                💪 特訓する (1日1回無料！)
            </button>
        </div>

        <button 
            onClick={() => setActiveTab('deck')} 
            style={{ width: '100%', marginBottom: '30px', background: 'linear-gradient(135deg, #1565c0, #0d47a1)', color: 'white', border: '2px solid #64b5f6', padding: '15px', borderRadius: '12px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 10px rgba(21, 101, 192, 0.4)' }}
        >
            ⚽️ 自分の最強スタメンを編成する
        </button>

        <div className="card">
          <h3 style={{ fontSize: '16px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>✏️ プロフィール編集</h3>
          
          <div style={{background:'#e3f2fd', padding:'15px', borderRadius:'10px', marginBottom:'20px', border:'1px solid #bbdefb'}}>
              <div style={{fontSize:'13px', fontWeight:'bold', color:'#1565c0', marginBottom:'8px'}}>
                  🔐 Googleログイン連携 <span style={{fontSize:'10px', fontWeight:'normal'}}>(PWA・通知用)</span>
              </div>
              <p style={{fontSize:'11px', color:'#555', marginBottom:'15px', lineHeight:'1.5'}}>
                  ホーム画面に追加したアプリ（PWA）からログインするための設定です。<br/>
                  下のボタンを押して、お使いのGoogleアカウントを安全に連携してください。
              </p>

              {user.googleUid ? (
                  <div style={{ padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #90caf9', textAlign: 'center', color: '#1565c0', fontWeight: 'bold', fontSize: '12px' }}>
                      ✅ 連携済みです
                  </div>
              ) : (
                  <button 
                      onClick={handleLinkGoogle} 
                      className="btn" 
                      style={{width: '100%', background: 'white', color: '#444', border: '1px solid #ccc', padding: '12px', borderRadius: '8px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)'}}
                  >
                      <img src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" alt="Google" style={{width:'18px', height:'18px'}} />
                      Googleアカウントを連携する
                  </button>
              )}
          </div>

          <div style={{background:'#fafafa', padding:'15px', borderRadius:'10px', marginBottom:'20px', border:'1px solid #eee'}}>
              <div style={{fontSize:'12px', fontWeight:'bold', color:'#555', marginBottom:'10px'}}>📞 連絡先・決済情報 (非公開)</div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontSize: '11px', color: '#999', display:'block', marginBottom:'2px' }}>携帯番号</label>
                <input type="tel" value={formData.phoneNumber} onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})} placeholder="090-1234-5678" />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontSize: '11px', color: '#999', display:'block', marginBottom:'2px' }}>メールアドレス</label>
                <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="example@mail.com" />
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontSize: '11px', color: '#999', display:'block', marginBottom:'2px' }}>PayPay ID</label>
                <input type="text" value={formData.paypayId} onChange={(e) => setFormData({...formData, paypayId: e.target.value})} placeholder="paypay_id" />
                <div style={{fontSize:'9px', color:'#ccc', marginTop:'2px'}}>※ポイント購入時の照合に使用します</div>
              </div>

              <div style={{ background: 'white', padding: '15px', borderRadius: '8px', border: '2px solid #2196F3', marginTop: '20px' }}>
                  <h4 style={{ color: '#1565C0', margin: '0 0 10px 0', fontSize: '14px', textAlign: 'center' }}>
                      🏟️ グラウンド取得協力者募集中
                  </h4>
                  <p style={{ fontSize: '11px', color: '#555', lineHeight: '1.5', marginBottom: '10px' }}>
                      都立公園のグラウンド抽選（スポーツレクリエーション予約システム）にご協力いただける方を募集しています！<br/>
                      <span style={{ color: '#e65100', fontWeight: 'bold' }}>🎁 特典：RBゲームシャツプレゼント・優先参加権</span>（さらに、ご協力いただいたIDで当選した場合は当月の参加費が無料になります！）
                  </p>

                  <div style={{ width: '100%', overflow: 'hidden' }}>
                      <div style={{ 
                          display: 'flex', 
                          flexWrap: 'nowrap', 
                          overflowX: 'auto', 
                          WebkitOverflowScrolling: 'touch',
                          gap: '10px', 
                          paddingBottom: '10px'
                      }}>
                          {[1, 2, 3, 4].map(num => (
                              <div key={num} style={{ 
                                  flex: '0 0 85%', 
                                  minWidth: '260px' 
                              }}>
                                  <img
                                      src={`${process.env.PUBLIC_URL}/images/id_guide_${num}.jpg?v=1`}
                                      alt={`ID協力手順 ${num}`}
                                      draggable="false" 
                                      style={{ 
                                          width: '100%', 
                                          aspectRatio: '3/4', 
                                          backgroundColor: '#eee', 
                                          objectFit: 'contain',
                                          borderRadius: '8px', 
                                          border: '1px solid #ddd', 
                                          display: 'block',
                                          pointerEvents: 'none',
                                          userSelect: 'none', 
                                          WebkitUserSelect: 'none'
                                      }}
                                  />
                              </div>
                          ))}
                      </div>
                  </div>
                  <div style={{ fontSize: '9px', color: '#999', textAlign: 'center', marginBottom: '10px' }}>
                      横にスワイプして手順を確認できます 👆↔️
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', background: formData.idCooperation ? '#e8f5e9' : '#f5f5f5', borderRadius: '8px', cursor: 'pointer', border: `2px solid ${formData.idCooperation ? '#4caf50' : '#ddd'}`, transition: '0.3s' }}>
                      <input
                          type="checkbox"
                          checked={formData.idCooperation}
                          onChange={(e) => setFormData({...formData, idCooperation: e.target.checked})}
                          style={{ transform: 'scale(1.5)', margin: 0, cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: formData.idCooperation ? '#2e7d32' : '#333' }}>
                          ID協力可能
                      </span>
                  </label>
              </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', color: COLORS.textLight, display:'block', marginBottom:'5px' }}>表示名 (必須)</label>
            <input type="text" value={formData.displayName} onChange={(e) => setFormData({...formData, displayName: e.target.value})} placeholder="例: 山田 太郎" />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', color: COLORS.textLight, display:'block', marginBottom:'5px' }}>生年月日</label>
            <input type="date" value={formData.birthDate} onChange={(e) => setFormData({...formData, birthDate: e.target.value})} style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>

          <div style={{ marginBottom: '15px' }}>
              <label style={{ fontSize: '12px', color: COLORS.textLight, display:'block', marginBottom:'5px' }}>性別</label>
              <div style={{display:'flex', gap:'10px'}}>
                  {['男性','女性','その他','未設定'].map(g => (
                      <button key={g} onClick={()=>setFormData({...formData, gender: g})} className="btn" style={{flex:1, padding:'8px', border: formData.gender===g ? `2px solid ${COLORS.primary}` : '1px solid #ddd', background: formData.gender===g ? '#e3f2fd' : 'white', color: formData.gender===g ? COLORS.primary : '#666', fontSize: '12px'}}>
                          {g}
                      </button>
                  ))}
              </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', color: COLORS.textLight, display:'block', marginBottom:'5px' }}>背番号</label>
            <input
                type="number"
                inputMode="numeric"
                min="0"
                max="999"
                value={formData.number}
                onChange={(e) => {
                    // ★背番号は 0〜999 の整数のみ許可。
                    //   小数点・電話番号・貼り付けなどで巨大な値や小数が入るのを防ぐ。
                    //   （過去に number へ電話番号や連結値が入り、一覧の表示が壊れた実績あり）
                    let v = e.target.value.replace(/[^0-9]/g, ''); // 数字以外を除去
                    if (v.length > 3) v = v.slice(0, 3);            // 3桁まで
                    if (v !== '' && Number(v) > 999) v = '999';
                    setFormData({ ...formData, number: v });
                }}
                placeholder="No. (0〜999)"
            />
          </div>

          <div style={{ background: formData.mainPosition === '未設定' ? '#ffebee' : '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '15px', border: formData.mainPosition === '未設定' ? '2px solid #d32f2f' : '1px solid #ddd' }}>
            <label style={{ fontSize: '12px', color: formData.mainPosition === '未設定' ? '#d32f2f' : COLORS.textLight, display:'block', marginBottom:'10px', fontWeight: 'bold' }}>
                🎮 ポジション設定 (シミュレーター連動)
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                    <label style={{ fontSize: '10px', color: '#666', display:'block', marginBottom:'2px' }}>大カテゴリ</label>
                    <select 
                        value={formData.mainPosition} 
                        onChange={(e) => {
                            const newMain = e.target.value;
                            const safeSubPositions = POSITIONS[newMain] || POSITIONS['未設定'];
                            setFormData({...formData, mainPosition: newMain, subPosition: safeSubPositions[0]});
                        }}
                        style={{ border: formData.mainPosition === '未設定' ? '1px solid #d32f2f' : '1px solid #ccc' }}
                    >
                        {Object.keys(POSITIONS).map(main => <option key={main} value={main}>{main}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ fontSize: '10px', color: '#666', display:'block', marginBottom:'2px' }}>詳細カテゴリ</label>
                    <select 
                        value={formData.subPosition} 
                        onChange={(e) => setFormData({...formData, subPosition: e.target.value})}
                        disabled={formData.mainPosition === '未設定'}
                        style={{ background: formData.mainPosition === '未設定' ? '#eee' : 'white' }}
                    >
                        {currentSubPositions.map(sub => <option key={sub} value={sub}>{sub}</option>)}
                    </select>
                </div>
            </div>
            {formData.mainPosition === '未設定' && (
                <div style={{ fontSize: '10px', color: '#d32f2f', marginTop: '8px', fontWeight: 'bold' }}>
                    ※得意なポジションを設定してください。
                </div>
            )}
          </div>

          <div style={{ marginBottom: '15px' }}>
              <label style={{ fontSize: '12px', color: COLORS.textLight, display:'block', marginBottom:'5px' }}>利き足</label>
              <div style={{display:'flex', gap:'10px'}}>
                  {['右','左','両足'].map(foot => (
                      <button key={foot} onClick={()=>setFormData({...formData, dominantFoot: foot})} className="btn" style={{flex:1, padding:'8px', border: formData.dominantFoot===foot ? `2px solid ${COLORS.primary}` : '1px solid #ddd', background: formData.dominantFoot===foot ? '#e3f2fd' : 'white', color: formData.dominantFoot===foot ? COLORS.primary : '#666'}}>
                          {foot}
                      </button>
                  ))}
              </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
            <div>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:'5px'}}>
                  <label style={{ fontSize: '12px', color: COLORS.textLight }}>身長 (cm)</label>
                  <label style={{fontSize:'10px', color:'#666'}}><input type="checkbox" checked={!formData.isPublicHeight} onChange={e=>setFormData({...formData, isPublicHeight: !e.target.checked})} style={{width:'auto'}} /> 非公開</label>
              </div>
              <input type="number" value={formData.height} onChange={(e) => setFormData({...formData, height: e.target.value})} placeholder="cm" />
            </div>
            <div>
              <div style={{display:'flex', justifyContent:'space-between', marginBottom:'5px'}}>
                  <label style={{ fontSize: '12px', color: COLORS.textLight }}>体重 (kg)</label>
                  <label style={{fontSize:'10px', color:'#666'}}><input type="checkbox" checked={!formData.isPublicWeight} onChange={e=>setFormData({...formData, isPublicWeight: !e.target.checked})} style={{width:'auto'}} /> 非公開</label>
              </div>
              <input type="number" value={formData.weight} onChange={(e) => setFormData({...formData, weight: e.target.value})} placeholder="kg" />
            </div>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', color: COLORS.textLight, display:'block', marginBottom:'5px' }}>プレースタイル</label>
            <input type="text" value={formData.playStyle} onChange={(e) => setFormData({...formData, playStyle: e.target.value})} placeholder="例: ドリブラー、ハードワーク..." />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontSize: '12px', color: COLORS.textLight, display:'block', marginBottom:'5px' }}>好きなチーム / 選手</label>
            <input type="text" value={formData.favoriteTeam} onChange={(e) => setFormData({...formData, favoriteTeam: e.target.value})} placeholder="例: アーセナル、三笘薫" />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontSize: '12px', color: COLORS.textLight, display:'block', marginBottom:'5px' }}>ひとこと・自己紹介</label>
            <textarea value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} placeholder="よろしくお願いします！" rows="3" />
          </div>

          <div style={{ background: '#ffebee', padding: '15px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #ffcdd2' }}>
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#c62828', marginBottom: '10px' }}>🛌 休部・引退設定</div>
              <p style={{ fontSize: '11px', color: '#d32f2f', marginBottom: '10px', lineHeight: '1.5' }}>
                  ※長期間お休みされる場合や、引退される場合はチェックを入れてください。<br/>
                  チェックを入れると、ガチャの排出対象から外れます。
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                      type="checkbox"
                      checked={formData.isRetired}
                      onChange={(e) => setFormData({...formData, isRetired: e.target.checked})}
                      style={{ transform: 'scale(1.3)', margin: 0, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#c62828' }}>休部・引退する（ガチャ排出停止）</span>
              </label>
          </div>

          <div style={{ fontSize: '10px', color: '#e65100', textAlign: 'center', marginBottom: '10px', fontWeight: 'bold', lineHeight: '1.4' }}>
              ※ステータスを保存すると、RBカードガチャの排出対象（カード化）として登録されます。<br/>同意の上、保存してください。
          </div>
          
          <button onClick={handleSave} disabled={loading} className="btn btn-primary btn-full">
            {loading ? '保存中...' : 'プロフィールとステータスを保存'}
          </button>
        </div>
        
        <NotificationToggle user={user} />

        <div style={{marginTop:'30px', textAlign:'center'}}>
            <button onClick={handleLogout} style={{background:'none', border:'none', color:'#d32f2f', fontSize:'12px', textDecoration:'underline', cursor:'pointer'}}>ログアウト / キャッシュクリア</button>
        </div>

        <div style={{height:'150px'}}></div>
      </div>
    </div>
  );
};

export default Profile;